import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Activity, Heart } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import PersonalParametersSection from './PersonalParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import GoalSettingSection from './GoalSettingSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { calculateBMI, getBMICategory, calculateIdealWeight, calculateCalories } from '../../../../../utils/calculations/health';

/**
 * Компонент основной формы расчета ИМТ
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  activityLevels,
  bodyTypes,
  weightGoals,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalculationName, setSaveCalculationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };
      
      // При смене системы измерения пересчитываем значения
      if (name === 'measurementSystem') {
        if (value === 'imperial' && prev.measurementSystem === 'metric') {
          // Конвертируем из метрических в имперские
          const heightCm = parseFloatOrDefault(prev.height, 0);
          const weightKg = parseFloatOrDefault(prev.weight, 0);
          
          newState.height = String(Math.round(heightCm / 2.54)); // см в дюймы
          newState.weight = String(Math.round(weightKg * 2.20462)); // кг в фунты
        } 
        else if (value === 'metric' && prev.measurementSystem === 'imperial') {
          // Конвертируем из имперских в метрические
          const heightInches = parseFloatOrDefault(prev.height, 0);
          const weightLbs = parseFloatOrDefault(prev.weight, 0);
          
          newState.height = String(Math.round(heightInches * 2.54)); // дюймы в см
          newState.weight = String(Math.round(weightLbs / 2.20462)); // фунты в кг
        }
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      weight, height, age, gender, 
      activityLevel, measurementSystem, bodyType, goal
    } = formData;

    // Парсинг числовых значений
    const numWeight = parseFloatOrDefault(weight, -1);
    const numHeight = parseFloatOrDefault(height, -1);
    const numAge = parseIntOrDefault(age, -1);

    // Валидация базовых полей
    if (numWeight <= 0) newErrors.weight = 'Вес должен быть положительным числом.';
    if (measurementSystem === 'metric' && numWeight > 300) 
      newErrors.weight = 'Вес не может превышать 300 кг.';
    else if (measurementSystem === 'imperial' && numWeight > 660) 
      newErrors.weight = 'Вес не может превышать 660 фунтов.';
    
    if (numHeight <= 0) newErrors.height = 'Рост должен быть положительным числом.';
    if (measurementSystem === 'metric' && numHeight > 250) 
      newErrors.height = 'Рост не может превышать 250 см.';
    else if (measurementSystem === 'imperial' && numHeight > 98) 
      newErrors.height = 'Рост не может превышать 98 дюймов (8 футов 2 дюйма).';
    
    if (!isInteger(age) || numAge < 0) newErrors.age = 'Возраст должен быть целым положительным числом.';
    if (numAge > 120) newErrors.age = 'Возраст не может превышать 120 лет.';
    
    if (!gender) newErrors.gender = 'Пожалуйста, укажите пол.';
    if (!activityLevel) newErrors.activityLevel = 'Пожалуйста, укажите уровень активности.';
    if (!bodyType) newErrors.bodyType = 'Пожалуйста, укажите тип телосложения.';
    if (!goal) newErrors.goal = 'Пожалуйста, укажите вашу цель.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета ИМТ и связанных показателей
  const calculateBmiResults = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const {
          weight, height, age, gender, 
          activityLevel, measurementSystem, bodyType, goal
        } = formData;

        // Конвертируем значения в метрическую систему если нужно
        let weightKg = parseFloatOrDefault(weight, 0);
        let heightCm = parseFloatOrDefault(height, 0);
        
        if (measurementSystem === 'imperial') {
          weightKg = weightKg / 2.20462; // фунты в кг
          heightCm = heightCm * 2.54; // дюймы в см
        }
        
        const ageYears = parseIntOrDefault(age, 30);
        
        // Находим множители для активности, телосложения и цели
        const activityFactor = activityLevels.find(a => a.id === activityLevel)?.factor || 1.2;
        const bodyTypeFactor = bodyTypes.find(b => b.id === bodyType)?.factor || 1.0;
        const goalFactor = weightGoals.find(g => g.id === goal)?.factor || 1.0;

        // --- Рассчитываем основные показатели ---
        
        // ИМТ
        const bmi = calculateBMI(weightKg, heightCm);
        
        // Категория ИМТ и рекомендации
        const bmiCategoryInfo = getBMICategory(bmi);
        
        // Идеальный вес по разным формулам
        const idealWeightBroca = calculateIdealWeight(heightCm, gender, 'broca');
        const idealWeightDevine = calculateIdealWeight(heightCm, gender, 'devine');
        const idealWeightMiller = calculateIdealWeight(heightCm, gender, 'miller');
        
        // Средний идеальный вес с поправкой на телосложение и цель
        const avgIdealWeight = (idealWeightBroca + idealWeightDevine + idealWeightMiller) / 3 * bodyTypeFactor * goalFactor;
        
        // Диапазон здорового веса (ИМТ 18.5 - 25)
        const minHealthyWeight = Math.round((18.5 * Math.pow(heightCm / 100, 2)) * 10) / 10;
        const maxHealthyWeight = Math.round((25 * Math.pow(heightCm / 100, 2)) * 10) / 10;
        
        // Расчет суточной нормы калорий
        const baseDailyCalories = calculateCalories(weightKg, heightCm, ageYears, gender, activityLevel);
        const goalDailyCalories = Math.round(baseDailyCalories * goalFactor);
        
        // Расчет отклонения от идеального веса
        const weightDifference = weightKg - avgIdealWeight;
        const weightDifferencePercent = (weightDifference / avgIdealWeight) * 100;
        
        // --- Формируем результаты ---
        const calculationResults = {
          bmi: Math.round(bmi * 10) / 10,
          category: bmiCategoryInfo.category,
          description: bmiCategoryInfo.description,
          recommendation: bmiCategoryInfo.recommendation,
          
          gender: gender,
          age: ageYears,
          height: heightCm,
          weight: weightKg,
          
          idealWeight: Math.round(avgIdealWeight * 10) / 10,
          idealWeightRange: {
            min: minHealthyWeight,
            max: maxHealthyWeight
          },
          
          weightDifference: Math.round(weightDifference * 10) / 10,
          weightDifferencePercent: Math.round(weightDifferencePercent * 10) / 10,
          
          dailyCalories: {
            base: baseDailyCalories,
            goal: goalDailyCalories
          },
          
          bodyTypeAdjustment: Math.round((bodyTypeFactor - 1) * 100),
          goalAdjustment: Math.round((goalFactor - 1) * 100),
          
          // Дополнительные показатели для анализа
          bmiAlternatives: {
            broca: idealWeightBroca,
            devine: idealWeightDevine,
            miller: idealWeightMiller
          }
        };

        setResults(calculationResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating, activityLevels, bodyTypes, weightGoals]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      weight: '70',
      height: '170',
      age: '30',
      gender: 'male',
      activityLevel: 'moderate',
      measurementSystem: 'metric',
      bodyType: 'normal',
      goal: 'maintain',
    });
    setResults(null);
    setErrors({});
    setSaveCalculationName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Подготовка данных для CSV
    const rows = [
      ['Параметр', 'Значение'],
      ['Дата расчета', new Date().toLocaleDateString('ru-RU')],
      [''],
      ['Личные параметры'],
      ['Пол', formData.gender === 'male' ? 'Мужской' : 'Женский'],
      ['Возраст', formData.age + ' лет'],
      ['Рост', formData.measurementSystem === 'metric' ? formData.height + ' см' : formData.height + ' дюймов'],
      ['Вес', formData.measurementSystem === 'metric' ? formData.weight + ' кг' : formData.weight + ' фунтов'],
      ['Тип телосложения', bodyTypes.find(type => type.id === formData.bodyType)?.name || formData.bodyType],
      ['Уровень активности', activityLevels.find(level => level.id === formData.activityLevel)?.name || formData.activityLevel],
      [''],
      ['Результаты расчета'],
      ['Индекс массы тела (ИМТ)', results.bmi.toString()],
      ['Категория ИМТ', results.category],
      ['Описание', results.description],
      ['Идеальный вес', results.idealWeight + ' кг'],
      ['Диапазон здорового веса', results.idealWeightRange.min + ' - ' + results.idealWeightRange.max + ' кг'],
      ['Отклонение от идеального веса', (results.weightDifference > 0 ? '+' : '') + results.weightDifference + ' кг (' + (results.weightDifferencePercent > 0 ? '+' : '') + results.weightDifferencePercent + '%)'],
      ['Суточная норма калорий (основная)', results.dailyCalories.base + ' ккал'],
      ['Суточная норма калорий (с учетом цели)', results.dailyCalories.goal + ' ккал'],
      [''],
      ['Рекомендации'],
      ['', results.recommendation]
    ];

    // Преобразование в CSV-строку
    const csvContent = '\uFEFF' + rows.map(row => row.join(';')).join('\n');
    
    // Создание и скачивание файла
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'индекс_массы_тела.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalculationName || null);
      if (savedResult) {
        alert(`Расчет ИМТ "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalculationName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateBmiResults();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Activity className="w-6 h-6 text-emerald-500" />
          Расчет индекса массы тела (ИМТ)
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры */}
        <PersonalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          activityLevels={activityLevels}
          bodyTypes={bodyTypes}
        />

        {/* Настройка целей */}
        <GoalSettingSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          weightGoals={weightGoals}
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalculationName}
                onChange={(e) => setSaveCalculationName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать ИМТ'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать результаты (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;